import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'

import Dialog from './Dialog'
import Select from './Select'
import AutoComplete from './AutoComplete'
import GridCheckbox from './GridCheckbox'
import TableMatchSelection from './table/TableMatchSelection'
import GridCircularProgress from './GridCircularProgress'
import { titlesIscrizioni } from '../settings/settings'
import { sortNumeriGara } from '../hooks/sort-functions'
import {
  useGetCalendariQuery,
  useGetImpostazioniAvanzateQuery,
  useGetImpostazioniContattiQuery,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'
import { mailTo } from '../hooks/utils-functions'
import { getCategorie } from '../hooks/selection-get-functions'

const AVANZ_FIELDS = ['GiorniAnticipoArbitraggio']

const DialogRichiestaArbitraggio = ({ open, handleClose, triggerFunction }) => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const selectedCampionatoGironiPubblicati = useSelector(
    state => state.selection.selectedCampionatoGironiPubblicati,
  )

  const { data: iscrizioniLight } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'light' },
    { skip: !(selectedCampionato && selectedCampionatoGironiPubblicati) },
  )
  const { data: iscrizioni } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, codiceSocieta: !admin && codiceUtente },
    { skip: !(selectedCampionato && (admin || codiceUtente)) },
  )

  const { data: availableGare, isLoading } = useGetCalendariQuery(
    { selectedCampionato, codiceUtente, tipologia: 'arbitraggio', admin },
    { skip: !(selectedCampionato && (admin || codiceUtente)) },
  )

  const { GiorniAnticipoArbitraggio } = avanzate || {}

  const [availableCategorie, setAvailableCategorie] = useState()
  const [availableSquadre, setAvailableSquadre] = useState()
  const [availableGareTeam, setAvailableGareTeam] = useState()
  const [availableGareCategoria, setAvailableGareCategoria] = useState()
  const [selectedCategoria, setSelectedCategoria] = useState()
  const [selectedSquadra, setSelectedSquadra] = useState()
  const [selectedGaraInfo, setSelectedGaraInfo] = useState()
  const [selectedGare, setSelectedGare] = useState()
  const [checkPayment, setCheckPayment] = useState()

  // Set categorie
  useEffect(() => {
    if (open) setAvailableCategorie(getCategorie(availableGare))
  }, [availableGare, open])

  // Set gare
  useEffect(() => {
    setAvailableGareCategoria(
      availableGare
        ?.filter(a => a.categoria === selectedCategoria)
        ?.sort((a, b) => sortNumeriGara(a.numeroGara, b.numeroGara)),
    )
  }, [availableGare, selectedCategoria])

  // Set squadre
  useEffect(() => {
    setAvailableSquadre(
      iscrizioniLight
        ?.filter(i =>
          availableGareCategoria?.map(a => a.squadraCasaID)?.includes(i.id),
        )
        ?.map(i => i.id),
    )
  }, [availableGareCategoria, iscrizioniLight])

  // Set gare
  useEffect(() => {
    setAvailableGareTeam(
      availableGare
        ?.filter(
          a =>
            a.squadraCasaID === selectedSquadra ||
            a.squadraTrasfertaID === selectedSquadra,
        )
        ?.sort((a, b) => sortNumeriGara(a.numeroGara, b.numeroGara)),
    )
  }, [availableGare, selectedSquadra])

  // Set selected gara info
  useEffect(() => {
    setSelectedGaraInfo(availableGare?.find(a => a._id === selectedGare?.[0]))
  }, [availableGare, selectedGare])

  useEffect(() => {
    setSelectedGare()
    setCheckPayment()
  }, [selectedSquadra])

  useEffect(() => {
    if (!open) {
      setSelectedCategoria()
      setSelectedSquadra()
    }
    setSelectedGare()
    setCheckPayment()
  }, [open])

  const GridWarning = ({ text }) => (
    <Grid size={12}>
      <center>
        <b>
          <i>{text}</i>
        </b>
      </center>
    </Grid>
  )

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={() => triggerFunction(selectedGare, checkPayment)}
      title={titlesIscrizioni.titleArbitraggio}
      textConfirm='Conferma'
      maxWidth='md'
      stretchHeight
    >
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {((!iscrizioni || iscrizioni.length === 0) && (
          <Grid size={12}>Nessuna squadra iscritta a questo campionato.</Grid>
        )) || (
          <Grid container sx={{ width: '100%' }}>
            <Grid size={12}>
              {(admin && (
                <>
                  Il presente modulo permette di richiedere un arbitraggio per
                  conto di una società.
                  <br />
                  Questa funzionalità è pensata per <b>casi eccezionali</b> in
                  cui la società richiedente non può inserire la richiesta da
                  sè.
                </>
              )) || (
                <>
                  Il presente modulo permette di richiedere l'arbitraggio di una
                  o più gare. Selezionarle dalla tabella sottostante, dopo aver
                  scelto la categoria e la squadra, poi spuntare la casella che
                  appare in basso per confermare l'impegno a versare la tassa
                  per l'arbitraggio.
                  <br />
                  <br />
                  <b>
                    La richiesta va inoltrata
                    {GiorniAnticipoArbitraggio
                      ? ` almeno ${GiorniAnticipoArbitraggio} giorni `
                      : ' '}
                    prima dell'orario di inizio gara e può essere effettuata
                    solo dalla squadra di casa.
                  </b>
                  <br />
                  <br />
                  Per domande o chiarimenti scrivere a{' '}
                  {mailTo(contatti?.MailPrincipale)}.
                </>
              )}
            </Grid>
            {(isLoading && <GridCircularProgress />) ||
              (!availableGare?.length && (
                <GridWarning text='Nessuna gara idonea.' />
              )) ||
              (admin && (
                <>
                  <Grid container spacing={2} sx={{ width: '100%' }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Select
                        id='categoria'
                        label='Categoria'
                        value={selectedCategoria}
                        options={availableCategorie}
                        onChange={i => {
                          setSelectedGare()
                          setSelectedCategoria(i)
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AutoComplete
                        id='gara'
                        label='Gara'
                        value={selectedGare?.[0]}
                        options={availableGareCategoria?.map(i => i._id)}
                        getOptionLabel={i =>
                          availableGareCategoria?.find(a => a._id === i)
                            ?.numeroGara
                        }
                        onChange={i => setSelectedGare([i])}
                        disabled={!selectedCategoria}
                      />
                    </Grid>
                  </Grid>
                  {selectedGare?.[0] && (
                    <Grid
                      container
                      spacing={1}
                      alignItems='center'
                      sx={{ width: '100%' }}
                    >
                      <Grid size={{ xs: 12, md: 6 }}>
                        <center>
                          <b>
                            {iscrizioniLight?.find(
                              i => i.id === selectedGaraInfo?.squadraCasaID,
                            )?.nomeSquadra || selectedGaraInfo?.squadraCasaID}
                          </b>
                          &nbsp;-&nbsp;
                          <b>
                            {iscrizioniLight?.find(
                              i =>
                                i.id === selectedGaraInfo?.squadraTrasfertaID,
                            )?.nomeSquadra ||
                              selectedGaraInfo?.squadraTrasfertaID}
                          </b>
                        </center>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <center>
                          {selectedGaraInfo?.data || 'Data da definire'}
                        </center>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <center>
                          {selectedGaraInfo?.ora || 'Ora da definire'}
                        </center>
                      </Grid>
                    </Grid>
                  )}
                </>
              )) || (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                      label='Categoria'
                      value={selectedCategoria}
                      options={availableCategorie}
                      onChange={setSelectedCategoria}
                      disabled={!availableCategorie}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Select
                      label='Squadra'
                      value={selectedSquadra}
                      options={availableSquadre}
                      optionLabels={availableSquadre?.map(
                        x =>
                          iscrizioniLight?.find(i => i.id === x)?.nomeSquadra,
                      )}
                      onChange={setSelectedSquadra}
                      disabled={!selectedCategoria || !availableSquadre}
                    />
                  </Grid>
                  {selectedSquadra && (
                    <TableMatchSelection
                      matches={availableGareTeam}
                      selectedGare={selectedGare}
                      setSelectedGare={setSelectedGare}
                      iscrizioniLight={iscrizioniLight}
                      selectedSquadra={selectedSquadra}
                      multiple={true}
                      showOpponentName={true}
                    />
                  )}
                  {selectedGare?.length > 0 && (
                    <GridCheckbox
                      label="La società si impegna a versare la tassa per l'arbitraggio."
                      value={!!checkPayment}
                      onChange={(_, x) => setCheckPayment(x)}
                      md={12}
                      justifyContent='flex-start'
                    />
                  )}
                </>
              )}
          </Grid>
        )}
      </Grid>
    </Dialog>
  )
}

export default DialogRichiestaArbitraggio
